import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ModerationNav } from '../components/ModerationNav';
import {
  fetchRestrictedUsers,
  reinstateUser,
  type MeResponse,
  type RestrictedUser,
} from '../lib/api';
import { promptDialog } from '../lib/dialog';

/** Relative timestamp helper — "3 days ago", "just now". */
function fmtRelative(iso: string): string {
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  return `${Math.floor(days / 365)} year(s) ago`;
}

/**
 * Moderator surface for finding and un-restricting accounts. Lists the
 * community's suspended + banned members; clicking Reinstate flips
 * their user.status back to 'active'. Ended conversations stay
 * ended — the user is just back in the matching pool on the next
 * pass.
 *
 * This is the symmetric counterpart to the suspend/ban actions that
 * live on a moderation_event's resolution. Useful when an appeal lands
 * or a moderator mistake is caught downstream.
 */
export function RestrictedUsers({ me }: { me: MeResponse }) {
  const communityId = me.memberships[0]?.communityId ?? '';
  const [rows, setRows] = useState<RestrictedUser[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!communityId) return;
    void fetchRestrictedUsers(communityId)
      .then(setRows)
      .catch(() => setRows([]));
  }, [communityId]);

  useEffect(load, [load]);

  async function reinstate(target: RestrictedUser) {
    if (busy) return;
    const note = await promptDialog({
      title: `Reinstate ${target.nickname}?`,
      message: `They are currently ${target.status}. Reinstating flips user.status back to active; ended conversations stay ended.`,
      inputLabel: 'Audit-log note (optional)',
      placeholder: 'Why are you reinstating them?',
      multiline: true,
      confirmLabel: 'Reinstate',
    });
    if (note === null) return;
    setBusy(target.userId);
    setError(null);
    const result = await reinstateUser(communityId, target.userId, note || undefined);
    setBusy(null);
    if (result.ok) {
      setRows((prev) => prev?.filter((r) => r.userId !== target.userId) ?? null);
    } else {
      setError(result.error);
    }
  }

  return (
    <main className="wide">
      <div className="card">
        <ModerationNav />
        <p className="step-label">
          <Link to="/moderation">← Review queue</Link>
        </p>
        <h1>Restricted users</h1>
        <p className="muted">
          Accounts that are currently suspended or banned in this community. Reinstating flips
          user.status back to active; ended conversations stay ended.
        </p>
        {error && <p className="error">{error}</p>}
        {rows === null && <p className="muted">Loading…</p>}
        {rows !== null && rows.length === 0 && (
          <p className="muted">No restricted users in this community. Good.</p>
        )}
        {rows && rows.length > 0 && (
          <ul className="bars restricted-users-list">
            {rows.map((r) => (
              <li key={r.userId}>
                <span className="bars-label">
                  <Link to={`/moderation/users/${r.userId}`}>{r.name || r.nickname}</Link>{' '}
                  <span className="status-pill status-banned">{r.status}</span>
                </span>
                <span className="bars-meta restricted-user-meta">
                  <span>{r.email}</span>
                  <span>nickname {r.nickname}</span>
                  {r.actionedAt && (
                    <span>
                      {r.actionedBy === 'user.banned' ? 'banned ' : 'suspended '}
                      {fmtRelative(r.actionedAt)}
                    </span>
                  )}
                  <Link to={`/moderation/users/${r.userId}`}>See why →</Link>
                </span>
                <span className="bars-value">
                  <button
                    type="button"
                    disabled={busy === r.userId}
                    onClick={() => void reinstate(r)}
                  >
                    {busy === r.userId ? 'Reinstating…' : 'Reinstate'}
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </main>
  );
}
